/**
 * AnalyticalReviewTab - Main component for analytical review with graphs and AI
 * 
 * This component orchestrates:
 * 1. Deterministic metrics building from SIE data
 * 2. Signal generation using configurable rules
 * 3. AI annotation fetching (optional)
 * 4. Interactive visualization
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Sparkles, 
  LineChart, 
  AlertTriangle, 
  Loader2,
  RefreshCw,
  Info,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

import type { SIEProcessedData } from '../types';
import type { MaterialityThresholds } from '@/lib/modules/types';
import type { MetricSeries, Signal, AIAnnotation } from './types';

import { buildMetrics, calculateTopDrivers } from './metrics-builder';
import { generateSignals, countSignalsBySeverity, getSignalsForMetric } from './signal-engine';
import { MetricChart } from './MetricChart';
import { SignalList, SignalSummary } from './SignalList';
import { AnnotationPanel, AnnotationCount } from './AnnotationPanel';

import type { DisplayFormat } from '../utils';

interface AnalyticalReviewTabProps {
  currentYear: SIEProcessedData;
  previousYear?: SIEProcessedData;
  thresholds?: MaterialityThresholds | null;
  auditId?: string;
  displayFormat?: DisplayFormat;
}

export function AnalyticalReviewTab({
  currentYear,
  previousYear,
  thresholds,
  auditId,
  displayFormat = 'auto',
}: AnalyticalReviewTabProps) {
  // State
  const [selectedMetric, setSelectedMetric] = useState<string>('REVENUE_TOTAL');
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [annotations, setAnnotations] = useState<AIAnnotation[]>([]);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiAvailable, setAiAvailable] = useState(true);
  
  // Build metrics from SIE data (deterministic)
  const metrics = useMemo(() => 
    buildMetrics(currentYear, previousYear),
    [currentYear, previousYear]
  );
  
  // Calculate top drivers per period
  const topDrivers = useMemo(() =>
    calculateTopDrivers(
      currentYear.aggregated.incomeStatementByAccountAndMonth || {},
      currentYear.accounts
    ),
    [currentYear]
  );
  
  // Generate signals (deterministic)
  const signals = useMemo(() => {
    const fiscalYearEnd = currentYear.metadata.fiscalYears[0]?.end?.slice(0, 7);
    return generateSignals(metrics, {
      spikeDropThreshold: 0.30,
      trendBreakThreshold: 0.20,
      thresholds,
      fiscalYearEnd,
    });
  }, [metrics, thresholds, currentYear.metadata]);
  
  // Signal counts for summary
  const signalCounts = useMemo(() => countSignalsBySeverity(signals), [signals]);
  
  // Current metric and its signals
  const currentMetric = useMemo(() => 
    metrics.find(m => m.metric_key === selectedMetric),
    [metrics, selectedMetric]
  );
  
  const currentMetricSignals = useMemo(() => 
    getSignalsForMetric(signals, selectedMetric),
    [signals, selectedMetric]
  );
  
  // Annotations filtered by current metric
  const currentAnnotations = useMemo(() => 
    annotations.filter(a => 
      currentMetric?.periods.includes(a.evidence.period)
    ),
    [annotations, currentMetric]
  );
  
  // Fetch AI annotations
  const fetchAnnotations = useCallback(async () => {
    setIsLoadingAI(true);
    setAiError(null);
    
    try {
      const fiscalYearLabel = currentYear.metadata.fiscalYears[0]?.end?.slice(0, 4) || 'N/A';
      
      const { data, error } = await supabase.functions.invoke('analytical-review-annotate', {
        body: {
          metrics: metrics.slice(0, 5), // Send only aggregate metrics to keep context small
          signals: signals.filter(s => s.severity !== 'LOW'), // Only significant signals
          top_drivers: topDrivers,
          fiscal_year_label: fiscalYearLabel,
        },
      });
      
      if (error) {
        // Check if it's an availability issue
        if (error.message?.includes('not found') || error.message?.includes('500')) {
          setAiAvailable(false);
          throw new Error('AI-tjänsten är för tillfället otillgänglig');
        }
        throw error;
      }
      
      if (data?.annotations) {
        setAnnotations(data.annotations);
      } else {
        setAnnotations([]);
      }
    } catch (err) {
      console.error('[AnalyticalReviewTab] AI annotation error:', err);
      setAiError(err instanceof Error ? err.message : 'Kunde inte hämta AI-insikter');
    } finally {
      setIsLoadingAI(false);
    }
  }, [metrics, signals, topDrivers, currentYear.metadata]);
  
  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <LineChart className="h-5 w-5" />
                Analytisk Granskning
              </CardTitle>
              <CardDescription>
                Månadsvis analys med automatiska signaler och AI-kommentarer
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <SignalSummary signals={signals} />
              <Button
                variant="outline"
                onClick={fetchAnnotations}
                disabled={isLoadingAI}
                className="gap-2"
              >
                {isLoadingAI ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : annotations.length > 0 ? (
                  <RefreshCw className="h-4 w-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {annotations.length > 0 ? 'Uppdatera AI-insikter' : 'Generera AI-insikter'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{metrics.length}</div>
              <div className="text-muted-foreground">Nyckeltal</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-destructive">{signalCounts.HIGH}</div>
              <div className="text-muted-foreground">Höga signaler</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold text-warning-foreground">{signalCounts.MEDIUM}</div>
              <div className="text-muted-foreground">Medel signaler</div>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <div className="text-2xl font-bold">{signalCounts.LOW}</div>
              <div className="text-muted-foreground">Låga signaler</div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Chart and Signals */}
        <div className="col-span-8 space-y-4">
          {/* Chart Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Månadsanalys</CardTitle>
              <Select value={selectedMetric} onValueChange={(v) => {
                setSelectedMetric(v);
                setSelectedPeriod(null);
              }}>
                <SelectTrigger className="w-[240px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Aggregate metrics first */}
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    Aggregerade nyckeltal
                  </div>
                  {metrics.filter(m => !m.metric_key.startsWith('ACCOUNT_')).map(m => (
                    <SelectItem key={m.metric_key} value={m.metric_key}>
                      {m.display_name}
                    </SelectItem>
                  ))}
                  
                  {/* Account metrics */}
                  {metrics.some(m => m.metric_key.startsWith('ACCOUNT_')) && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground mt-2">
                        Per konto
                      </div>
                      {metrics.filter(m => m.metric_key.startsWith('ACCOUNT_')).map(m => (
                        <SelectItem key={m.metric_key} value={m.metric_key}>
                          {m.display_name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {currentMetric ? (
                <MetricChart
                  metric={currentMetric}
                  signals={currentMetricSignals}
                  selectedPeriod={selectedPeriod}
                  onPeriodSelect={setSelectedPeriod}
                  showComparative={!!previousYear}
                  displayFormat={displayFormat}
                />
              ) : (
                <div className="h-[320px] flex items-center justify-center text-muted-foreground">
                  Välj ett nyckeltal för att visa graf
                </div>
              )}
              
              {selectedPeriod && (
                <Alert className="mt-4">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Visar data för <strong>{selectedPeriod}</strong>.{' '}
                    <button 
                      className="underline hover:no-underline"
                      onClick={() => setSelectedPeriod(null)}
                    >
                      Visa alla perioder
                    </button>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
          
          {/* Signals Card */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Signaler
                  <Badge variant="outline" className="ml-2">
                    {currentMetricSignals.length}
                  </Badge>
                </CardTitle>
              </div>
              <CardDescription>
                Automatiskt identifierade avvikelser för {currentMetric?.display_name || 'valt nyckeltal'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SignalList 
                signals={currentMetricSignals}
                selectedPeriod={selectedPeriod}
                onPeriodSelect={setSelectedPeriod}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* Right: AI Insights Panel */}
        <div className="col-span-4">
          <Card className="sticky top-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                AI-insikter
                <AnnotationCount annotations={currentAnnotations} />
              </CardTitle>
              <CardDescription>
                AI-tolkningar av identifierade signaler
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AnnotationPanel
                annotations={currentAnnotations}
                signals={currentMetricSignals}
                selectedPeriod={selectedPeriod}
                isLoading={isLoadingAI}
                error={aiError}
                aiAvailable={aiAvailable}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
